import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { SearchableSelect } from '../../components/common/SearchableSelect';
import { theme } from '../../theme';

const CATEGORIES = ['Technical', 'Soft Skills', 'Compliance', 'Leadership', 'Safety', 'Other'];
const LEVELS = ['novice', 'developing', 'proficient', 'advanced', 'expert'];
const LEVEL_COLORS = { novice: '#EF4444', developing: '#F97316', proficient: '#EAB308', advanced: '#22C55E', expert: '#3B82F6' };
const LEVEL_LABELS = { novice: 'Novice', developing: 'Developing', proficient: 'Proficient', advanced: 'Advanced', expert: 'Expert' };
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const STATUS_COLORS = { planned: '#3B82F6', ongoing: '#F97316', completed: '#22C55E', cancelled: '#EF4444' };

const INP = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', boxSizing: 'border-box', background: '#fff' };
const LBL = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' };
const FG = { marginBottom: '0.875rem' };

// ─── Training Programs Tab ────────────────────────────────────────────────────

function TrainingPrograms({ employees, departments }) {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [participantsModal, setParticipantsModal] = useState(null);
  const [addEmpIds, setAddEmpIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return { title: '', description: '', trainer_type: 'external', trainer: '', trainer_company: '', internal_trainer_id: '', mode: 'online', category: '', venue: '', department_id: '', start_date: '', end_date: '', max_participants: '', status: 'planned' };
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterCat) params.category = filterCat;
      const res = await hrms.getTrainingPrograms(params);
      setPrograms(res.data || []);
    } catch { } finally { setLoading(false); }
  }, [filterStatus, filterCat]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.title.trim()) { alert('Title is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        internal_trainer_id: form.internal_trainer_id ? parseInt(form.internal_trainer_id) : null,
        department_id: form.department_id ? parseInt(form.department_id) : null,
        max_participants: form.max_participants ? parseInt(form.max_participants) : null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      if (modal?.id) await hrms.updateTraining(modal.id, payload);
      else await hrms.createTraining(payload);
      setModal(null);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const handleAddParticipants = async () => {
    if (!addEmpIds.length) return;
    setSaving(true);
    try {
      await hrms.addTrainingParticipants(participantsModal.id, { employee_ids: addEmpIds.map(Number) });
      setAddEmpIds([]);
      const res = await hrms.getTrainingPrograms({});
      setPrograms(res.data || []);
      const updated = (res.data || []).find(p => p.id === participantsModal.id);
      if (updated) setParticipantsModal(updated);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const handleRemoveParticipant = async (empId) => {
    if (!window.confirm('Remove this participant?')) return;
    await hrms.removeTrainingParticipant(participantsModal.id, empId);
    const res = await hrms.getTrainingPrograms({});
    setPrograms(res.data || []);
    const updated = (res.data || []).find(p => p.id === participantsModal.id);
    if (updated) setParticipantsModal(updated);
  };

  const openEdit = (row) => {
    setForm({
      title: row.title || '', description: row.description || '',
      trainer_type: row.trainer_type || 'external', trainer: row.trainer || '',
      trainer_company: row.trainer_company || '',
      internal_trainer_id: row.internal_trainer_id || '',
      mode: row.mode || 'online', category: row.category || '',
      venue: row.venue || '', department_id: row.department_id || '',
      start_date: row.start_date || '', end_date: row.end_date || '',
      max_participants: row.max_participants || '', status: row.status || 'planned',
    });
    setModal(row);
  };

  const filtered = programs.filter(p => !search || p.title.toLowerCase().includes(search.toLowerCase()));

  const participantEnrolledIds = new Set((participantsModal?.participants || []).map(p => p.employee_id));

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Search trainings…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...INP, width: '220px', marginBottom: 0 }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...INP, width: '150px', marginBottom: 0 }}>
          <option value="">All Status</option>
          {['planned','ongoing','completed','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
        </select>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...INP, width: '160px', marginBottom: 0 }}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={() => { setModal({}); setForm(emptyForm()); }}
          style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.25rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
          + New Training
        </button>
      </div>

      {/* Table */}
      {loading ? <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading…</div> : (
        <div style={{ background: '#fff', borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: `1px solid ${theme.border}` }}>
                {['Title','Category','Trainer','Mode','Start Date','Participants','Status','Actions'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.78rem', color: '#6B7280', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: '3rem', textAlign: 'center', color: theme.textMuted }}>No training programs found</td></tr>
              )}
              {filtered.map((row, i) => (
                <tr key={row.id} style={{ borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: theme.text, maxWidth: '240px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</div>
                    {row.venue && <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>📍 {row.venue}</div>}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {row.category ? <span style={{ background: '#F3F4F6', color: '#374151', borderRadius: '1rem', padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 500 }}>{row.category}</span> : '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.text }}>
                    {row.trainer_type === 'internal'
                      ? <span>{row.internal_trainer_name || '—'}<span style={{ fontSize: '0.68rem', color: '#3B82F6', marginLeft: '0.3rem' }}>[Internal]</span></span>
                      : <span>{row.trainer || '—'}{row.trainer_company && <span style={{ fontSize: '0.7rem', color: theme.textMuted }}> · {row.trainer_company}</span>}</span>
                    }
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, fontSize: '0.8rem' }}>{row.mode?.charAt(0).toUpperCase() + row.mode?.slice(1)}</td>
                  <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {row.start_date ? new Date(row.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <button onClick={() => setParticipantsModal(row)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.primary, fontWeight: 600, fontSize: '0.85rem' }}>
                      {row.participant_count}/{row.max_participants || '∞'}
                    </button>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={{ background: STATUS_COLORS[row.status] + '20', color: STATUS_COLORS[row.status], borderRadius: '1rem', padding: '0.2rem 0.65rem', fontSize: '0.75rem', fontWeight: 600 }}>
                      {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button onClick={() => openEdit(row)} style={btnStyle('#6B7280')}>Edit</button>
                      <button onClick={async () => { if (window.confirm('Delete this training?')) { await hrms.deleteTraining(row.id); load(); } }} style={btnStyle('#EF4444')}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Training Form Modal */}
      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={modal?.id ? 'Edit Training' : 'New Training Program'} maxWidth="680px"
        footer={<><button onClick={() => setModal(null)} style={btnStyle('#6B7280')}>Cancel</button><button onClick={handleSave} disabled={saving} style={{ ...btnStyle(theme.primary), color: '#fff', background: theme.primary }}>{saving ? 'Saving…' : 'Save'}</button></>}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
          <div style={{ gridColumn: '1/-1', ...FG }}>
            <label style={LBL}>Training Title *</label>
            <input style={INP} value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Advanced React Development" />
          </div>
          <div style={{ gridColumn: '1/-1', ...FG }}>
            <label style={LBL}>Description</label>
            <textarea style={{ ...INP, minHeight: '70px', resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
          </div>
          <div style={FG}>
            <label style={LBL}>Category</label>
            <select style={INP} value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
              <option value="">— Select —</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={FG}>
            <label style={LBL}>Mode</label>
            <select style={INP} value={form.mode} onChange={e => setForm(f => ({...f, mode: e.target.value}))}>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          <div style={FG}>
            <label style={LBL}>Department (optional)</label>
            <select style={INP} value={form.department_id} onChange={e => setForm(f => ({...f, department_id: e.target.value}))}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div style={FG}>
            <label style={LBL}>Venue / Location</label>
            <input style={INP} value={form.venue} onChange={e => setForm(f => ({...f, venue: e.target.value}))} placeholder="Conference Room A / Zoom" />
          </div>

          {/* Trainer Section */}
          <div style={{ gridColumn: '1/-1', borderTop: `1px solid ${theme.border}`, paddingTop: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', marginBottom: '0.5rem' }}>TRAINER DETAILS</div>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
              {['external','internal'].map(t => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: form.trainer_type === t ? 600 : 400 }}>
                  <input type="radio" value={t} checked={form.trainer_type === t} onChange={() => setForm(f => ({...f, trainer_type: t}))} />
                  {t.charAt(0).toUpperCase() + t.slice(1)} Trainer
                </label>
              ))}
            </div>
          </div>
          {form.trainer_type === 'internal' ? (
            <div style={{ gridColumn: '1/-1', ...FG }}>
              <label style={LBL}>Select Internal Trainer</label>
              <SearchableSelect
                options={employees.map(e => ({ value: e.id, label: e.full_name, meta: e.emp_code || '' }))}
                value={form.internal_trainer_id}
                onChange={v => setForm(f => ({...f, internal_trainer_id: v || ''}))}
                placeholder="Search employee…"
              />
            </div>
          ) : (
            <>
              <div style={FG}>
                <label style={LBL}>Trainer Name</label>
                <input style={INP} value={form.trainer} onChange={e => setForm(f => ({...f, trainer: e.target.value}))} placeholder="Trainer full name" />
              </div>
              <div style={FG}>
                <label style={LBL}>Organization / Company</label>
                <input style={INP} value={form.trainer_company} onChange={e => setForm(f => ({...f, trainer_company: e.target.value}))} placeholder="Company or institution" />
              </div>
            </>
          )}

          <div style={FG}>
            <label style={LBL}>Start Date</label>
            <input type="date" style={INP} value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} />
          </div>
          <div style={FG}>
            <label style={LBL}>End Date</label>
            <input type="date" style={INP} value={form.end_date} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} />
          </div>
          <div style={FG}>
            <label style={LBL}>Max Participants</label>
            <input type="number" style={INP} value={form.max_participants} onChange={e => setForm(f => ({...f, max_participants: e.target.value}))} placeholder="Unlimited if blank" />
          </div>
          <div style={FG}>
            <label style={LBL}>Status</label>
            <select style={INP} value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
              {['planned','ongoing','completed','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Participants Modal */}
      <Modal isOpen={!!participantsModal} onClose={() => { setParticipantsModal(null); setAddEmpIds([]); }} title={`Participants — ${participantsModal?.title}`} maxWidth="700px"
        footer={<button onClick={() => { setParticipantsModal(null); setAddEmpIds([]); }} style={btnStyle('#6B7280')}>Close</button>}>
        {/* Add participants */}
        <div style={{ background: '#F9FAFB', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem', border: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.5rem', color: '#374151' }}>ADD PARTICIPANTS</div>
          <MultiEmployeeSelect
            employees={employees.filter(e => !participantEnrolledIds.has(e.id))}
            selected={addEmpIds}
            onChange={setAddEmpIds}
          />
          <button onClick={handleAddParticipants} disabled={!addEmpIds.length || saving}
            style={{ marginTop: '0.5rem', background: addEmpIds.length ? theme.primary : '#D1D5DB', color: '#fff', border: 'none', borderRadius: '0.375rem', padding: '0.5rem 1rem', cursor: addEmpIds.length ? 'pointer' : 'default', fontSize: '0.85rem', fontWeight: 600 }}>
            {saving ? 'Adding…' : `Add ${addEmpIds.length ? `(${addEmpIds.length})` : ''}`}
          </button>
        </div>

        {/* Participant list */}
        <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
          {(participantsModal?.participants || []).length === 0
            ? <div style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>No participants yet</div>
            : (participantsModal?.participants || []).map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', padding: '0.6rem 0', borderBottom: `1px solid ${theme.border}`, gap: '0.75rem' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: theme.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 }}>
                  {p.full_name?.charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.text }}>{p.full_name}</div>
                  <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{p.emp_code} · {p.department}</div>
                </div>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: '1rem', background: p.status === 'completed' ? '#D1FAE5' : '#EFF6FF', color: p.status === 'completed' ? '#065F46' : '#1D4ED8' }}>
                  {p.status}
                </span>
                {p.feedback_submitted && <span title="Feedback submitted" style={{ fontSize: '0.75rem', color: '#22C55E' }}>✓ Feedback</span>}
                <button onClick={() => handleRemoveParticipant(p.employee_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: '1.1rem', padding: '0.1rem 0.3rem' }}>×</button>
              </div>
            ))
          }
        </div>
      </Modal>
    </div>
  );
}

// Multi-employee select using checkboxes
function MultiEmployeeSelect({ employees, selected, onChange }) {
  const [q, setQ] = useState('');
  const filtered = employees.filter(e => `${e.full_name} ${e.emp_code}`.toLowerCase().includes(q.toLowerCase())).slice(0, 50);
  const toggle = (id) => onChange(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  return (
    <div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search employees…" style={{ ...INP, marginBottom: '0.5rem' }} />
      <div style={{ maxHeight: '160px', overflowY: 'auto', border: `1px solid ${theme.border}`, borderRadius: '0.375rem' }}>
        {filtered.map(e => (
          <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.75rem', cursor: 'pointer', borderBottom: `1px solid #F3F4F6`, background: selected.includes(e.id) ? '#FFF7ED' : '#fff' }}>
            <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggle(e.id)} />
            <span style={{ fontSize: '0.85rem', color: theme.text }}>{e.full_name}</span>
            <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>{e.emp_code}</span>
          </label>
        ))}
        {filtered.length === 0 && <div style={{ padding: '0.75rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.85rem' }}>No employees found</div>}
      </div>
    </div>
  );
}

// ─── Training Calendar Tab ────────────────────────────────────────────────────

function TrainingCalendar() {
  const today = new Date();
  const [view, setView] = useState('month'); // 'month' | 'year'
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [programs, setPrograms] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    hrms.getTrainingPrograms({ year: calYear }).then(r => setPrograms(r.data || [])).catch(() => {});
  }, [calYear]);

  const getProgramsForDate = (d) => {
    const ds = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    return programs.filter(p => p.start_date && p.start_date.slice(0,10) === ds);
  };

  const getProgramsForMonth = (m) => programs.filter(p => {
    if (!p.start_date) return false;
    const d = new Date(p.start_date);
    return d.getFullYear() === calYear && d.getMonth() === m;
  });

  function getCalDays(y, m) {
    const first = new Date(y, m, 1).getDay();
    const last = new Date(y, m+1, 0).getDate();
    const days = [];
    for (let i = 0; i < first; i++) days.push(null);
    for (let i = 1; i <= last; i++) days.push(new Date(y, m, i));
    return days;
  }

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['month','year'].map(v => (
            <button key={v} onClick={() => setView(v)} style={{ padding: '0.45rem 1rem', border: 'none', borderRadius: '0.375rem', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', background: view === v ? theme.primary : '#F3F4F6', color: view === v ? '#fff' : '#374151' }}>
              {v.charAt(0).toUpperCase()+v.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button onClick={() => { if (view === 'month') { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y-1); } else setCalMonth(m => m-1); } else setCalYear(y => y-1); }} style={navBtn}>‹</button>
          <span style={{ fontWeight: 700, fontSize: '1rem', color: theme.text, minWidth: '160px', textAlign: 'center' }}>
            {view === 'month' ? `${MONTH_FULL[calMonth]} ${calYear}` : calYear}
          </span>
          <button onClick={() => { if (view === 'month') { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y+1); } else setCalMonth(m => m+1); } else setCalYear(y => y+1); }} style={navBtn}>›</button>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: '0.8rem', color: theme.textMuted }}>{programs.length} training(s) in {calYear}</div>
      </div>

      {view === 'month' ? (
        <div style={{ background: '#fff', borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: theme.dark }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} style={{ padding: '0.6rem', textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.05em' }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
            {getCalDays(calYear, calMonth).map((d, i) => {
              if (!d) return <div key={`empty-${i}`} style={{ minHeight: '90px', borderRight: `1px solid ${theme.border}`, borderBottom: `1px solid ${theme.border}`, background: '#FAFAFA' }} />;
              const isToday = d.toDateString() === today.toDateString();
              const dayProgs = getProgramsForDate(d);
              return (
                <div key={i} style={{ minHeight: '90px', padding: '0.4rem', borderRight: `1px solid ${theme.border}`, borderBottom: `1px solid ${theme.border}`, background: isToday ? '#FFF7ED' : '#fff' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: isToday ? 800 : 500, color: isToday ? theme.primary : '#374151', width: '22px', height: '22px', borderRadius: '50%', background: isToday ? '#FEE2E2' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.25rem' }}>
                    {d.getDate()}
                  </div>
                  {dayProgs.map(p => (
                    <div key={p.id} onClick={() => setSelected(p)} title={p.title}
                      style={{ fontSize: '0.68rem', fontWeight: 600, background: STATUS_COLORS[p.status] + '25', color: STATUS_COLORS[p.status], borderRadius: '0.25rem', padding: '0.15rem 0.35rem', marginBottom: '0.2rem', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.title}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        // Year view
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem' }}>
          {MONTHS.map((m, mi) => {
            const mProgs = getProgramsForMonth(mi);
            const isCurrentMonth = calYear === today.getFullYear() && mi === today.getMonth();
            return (
              <div key={mi} onClick={() => { setCalMonth(mi); setView('month'); }}
                style={{ background: '#fff', borderRadius: '0.75rem', border: `2px solid ${isCurrentMonth ? theme.primary : theme.border}`, padding: '1rem', cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = theme.primary}
                onMouseLeave={e => e.currentTarget.style.borderColor = isCurrentMonth ? theme.primary : theme.border}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text, marginBottom: '0.5rem' }}>{MONTH_FULL[mi]}</div>
                {mProgs.length === 0
                  ? <div style={{ fontSize: '0.75rem', color: '#D1D5DB' }}>No trainings</div>
                  : mProgs.map(p => (
                    <div key={p.id} style={{ fontSize: '0.72rem', fontWeight: 500, color: STATUS_COLORS[p.status], marginBottom: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      · {p.title}
                    </div>
                  ))
                }
                {mProgs.length > 0 && <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: theme.textMuted }}>{mProgs.length} training{mProgs.length > 1 ? 's' : ''}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Training detail popover */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelected(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', width: '420px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.text, margin: 0, flex: 1 }}>{selected.title}</h3>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#9CA3AF' }}>×</button>
            </div>
            {[
              ['Category', selected.category],
              ['Mode', selected.mode?.charAt(0).toUpperCase()+selected.mode?.slice(1)],
              ['Venue', selected.venue],
              ['Trainer', selected.trainer_type === 'internal' ? selected.internal_trainer_name : `${selected.trainer || '—'}${selected.trainer_company ? ` (${selected.trainer_company})` : ''}`],
              ['Start', selected.start_date ? new Date(selected.start_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'],
              ['End', selected.end_date ? new Date(selected.end_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'],
              ['Participants', `${selected.participant_count}${selected.max_participants ? '/' + selected.max_participants : ''}`],
            ].filter(([,v]) => v).map(([k,v]) => (
              <div key={k} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', fontSize: '0.875rem' }}>
                <span style={{ color: theme.textMuted, minWidth: '90px' }}>{k}:</span>
                <span style={{ color: theme.text, fontWeight: 500 }}>{v}</span>
              </div>
            ))}
            <div style={{ marginTop: '0.75rem' }}>
              <span style={{ background: STATUS_COLORS[selected.status] + '20', color: STATUS_COLORS[selected.status], borderRadius: '1rem', padding: '0.25rem 0.75rem', fontSize: '0.78rem', fontWeight: 700 }}>
                {selected.status?.charAt(0).toUpperCase()+selected.status?.slice(1)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Skill Matrix Tab ─────────────────────────────────────────────────────────

function SkillMatrix({ departments }) {
  const [deptId, setDeptId] = useState('');
  const [cycleYear, setCycleYear] = useState(new Date().getFullYear());
  const [matrix, setMatrix] = useState(null);
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ratingModal, setRatingModal] = useState(null); // { employee, competency }
  const [ratingVal, setRatingVal] = useState('proficient');
  const [ratingNotes, setRatingNotes] = useState('');
  const [compModal, setCompModal] = useState(null);
  const [compForm, setCompForm] = useState({ name: '', description: '', department: '', category: '', expected_level: 'proficient' });
  const [saving, setSaving] = useState(false);

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const params = { cycle_year: cycleYear };
      if (deptId) params.department_id = deptId;
      const res = await hrms.getSkillMatrix(params);
      setMatrix(res.data);
    } catch { } finally { setLoading(false); }
  }, [deptId, cycleYear]);

  const loadCompetencies = useCallback(async () => {
    try {
      const params = {};
      if (deptId) params.department_id = deptId;
      const res = await hrms.getCompetenciesList(params);
      setCompetencies(res.data || []);
    } catch { }
  }, [deptId]);

  useEffect(() => { loadMatrix(); loadCompetencies(); }, [loadMatrix, loadCompetencies]);

  const handleRate = async () => {
    setSaving(true);
    try {
      await hrms.rateCompetency({ employee_id: ratingModal.employee.id, competency_id: ratingModal.competency.id, manager_level: ratingVal, cycle_year: cycleYear, notes: ratingNotes });
      setRatingModal(null);
      loadMatrix();
    } catch { } finally { setSaving(false); }
  };

  const handleSaveComp = async () => {
    if (!compForm.name.trim()) return;
    setSaving(true);
    try {
      if (compModal?.id) await hrms.updateCompetencyItem(compModal.id, compForm);
      else await hrms.createCompetencyItem(compForm);
      setCompModal(null);
      loadMatrix(); loadCompetencies();
    } catch { } finally { setSaving(false); }
  };

  const getLevelVsExpected = (actualLevel, expectedLevel) => {
    const idx = LEVELS.indexOf(actualLevel);
    const exp = LEVELS.indexOf(expectedLevel);
    if (idx < 0) return null;
    if (idx < exp) return 'below';
    if (idx === exp) return 'meets';
    return 'above';
  };

  const cellBg = (status) => ({ below: '#FEF2F2', meets: '#F0FDF4', above: '#EFF6FF', null: '#F9FAFB' }[status] || '#F9FAFB');
  const cellColor = (status) => ({ below: '#DC2626', meets: '#16A34A', above: '#2563EB', null: '#9CA3AF' }[status] || '#9CA3AF');

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={deptId} onChange={e => setDeptId(e.target.value)} style={{ ...INP, width: '200px', marginBottom: 0 }}>
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <input type="number" value={cycleYear} onChange={e => setCycleYear(Number(e.target.value))} style={{ ...INP, width: '100px', marginBottom: 0 }} />
        <div style={{ flex: 1 }} />
        <button onClick={() => { setCompModal({}); setCompForm({ name: '', description: '', department: '', category: '', expected_level: 'proficient' }); }}
          style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.1rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
          + Add Competency
        </button>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem', fontSize: '0.75rem' }}>
        {[['#FEF2F2','#DC2626','Below Expected'],['#F0FDF4','#16A34A','Meets Expected'],['#EFF6FF','#2563EB','Above Expected'],['#F9FAFB','#9CA3AF','Not Assessed']].map(([bg,cl,label]) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ width: '14px', height: '14px', borderRadius: '0.2rem', background: bg, border: `1.5px solid ${cl}`, display: 'inline-block' }} />
            <span style={{ color: '#6B7280' }}>{label}</span>
          </span>
        ))}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading matrix…</div> : !matrix ? null : (
        <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
            <thead>
              <tr style={{ background: theme.dark }}>
                <th style={{ ...TH, position: 'sticky', left: 0, zIndex: 3, background: theme.dark, minWidth: '160px', textAlign: 'left' }}>Employee</th>
                <th style={{ ...TH, position: 'sticky', left: 160, zIndex: 3, background: theme.dark, minWidth: '120px', textAlign: 'left', borderRight: `2px solid ${theme.primary}` }}>Department</th>
                {matrix.competencies.map(c => (
                  <th key={c.id} style={{ ...TH, minWidth: '110px' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700 }}>{c.name}</div>
                    <div style={{ fontSize: '0.62rem', color: '#9CA3AF' }}>{c.category}</div>
                    <div style={{ fontSize: '0.6rem', color: '#6B7280' }}>Exp: {LEVEL_LABELS[c.expected_level]}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.employees.length === 0 && (
                <tr><td colSpan={matrix.competencies.length + 2} style={{ padding: '3rem', textAlign: 'center', color: theme.textMuted }}>No employees found for selected department</td></tr>
              )}
              {matrix.employees.map((emp, ei) => (
                <tr key={emp.id} style={{ borderBottom: `1px solid ${theme.border}`, background: ei % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <td style={{ padding: '0.6rem 0.75rem', position: 'sticky', left: 0, zIndex: 1, background: ei % 2 === 0 ? '#fff' : '#FAFAFA', fontWeight: 500, color: theme.text }}>
                    {emp.full_name}<div style={{ fontSize: '0.68rem', color: theme.textMuted }}>{emp.emp_code}</div>
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem', position: 'sticky', left: 160, zIndex: 1, background: ei % 2 === 0 ? '#fff' : '#FAFAFA', color: theme.textMuted, borderRight: `2px solid rgba(249,115,22,0.15)` }}>
                    {emp.department || '—'}
                  </td>
                  {matrix.competencies.map(c => {
                    const r = emp.ratings[String(c.id)];
                    const level = r?.manager_level;
                    const status = level ? getLevelVsExpected(level, c.expected_level) : null;
                    return (
                      <td key={c.id} style={{ padding: '0.4rem 0.5rem', textAlign: 'center', cursor: 'pointer' }}
                        onClick={() => { setRatingModal({ employee: emp, competency: c }); setRatingVal(level || 'proficient'); setRatingNotes(r?.notes || ''); }}>
                        <div style={{ background: cellBg(status), color: cellColor(status), borderRadius: '0.25rem', padding: '0.25rem 0.4rem', fontSize: '0.7rem', fontWeight: 600, border: `1px solid ${cellColor(status)}30` }}>
                          {level ? LEVEL_LABELS[level] : '—'}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rating Modal */}
      <Modal isOpen={!!ratingModal} onClose={() => setRatingModal(null)} title="Rate Competency" maxWidth="420px"
        footer={<><button onClick={() => setRatingModal(null)} style={btnStyle('#6B7280')}>Cancel</button><button onClick={handleRate} disabled={saving} style={{ ...btnStyle(theme.primary), color: '#fff', background: theme.primary }}>{saving ? 'Saving…' : 'Save Rating'}</button></>}>
        {ratingModal && (
          <>
            <div style={{ marginBottom: '1rem', background: '#F9FAFB', borderRadius: '0.5rem', padding: '0.75rem' }}>
              <div style={{ fontSize: '0.8rem', color: theme.textMuted }}>Employee</div>
              <div style={{ fontWeight: 600, color: theme.text }}>{ratingModal.employee.full_name}</div>
              <div style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.5rem' }}>Competency</div>
              <div style={{ fontWeight: 600, color: theme.text }}>{ratingModal.competency.name}</div>
              <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>Expected: {LEVEL_LABELS[ratingModal.competency.expected_level]}</div>
            </div>
            <div style={FG}>
              <label style={LBL}>Manager Rating</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {LEVELS.map(l => (
                  <button key={l} onClick={() => setRatingVal(l)} style={{ padding: '0.4rem 0.75rem', border: `2px solid ${ratingVal === l ? LEVEL_COLORS[l] : theme.border}`, borderRadius: '0.375rem', background: ratingVal === l ? LEVEL_COLORS[l] + '20' : '#fff', color: ratingVal === l ? LEVEL_COLORS[l] : '#374151', fontWeight: ratingVal === l ? 700 : 400, fontSize: '0.8rem', cursor: 'pointer' }}>
                    {LEVEL_LABELS[l]}
                  </button>
                ))}
              </div>
            </div>
            <div style={FG}>
              <label style={LBL}>Notes (optional)</label>
              <textarea style={{ ...INP, minHeight: '70px', resize: 'vertical' }} value={ratingNotes} onChange={e => setRatingNotes(e.target.value)} placeholder="Development notes or observations…" />
            </div>
          </>
        )}
      </Modal>

      {/* Add Competency Modal */}
      <Modal isOpen={!!compModal} onClose={() => setCompModal(null)} title={compModal?.id ? 'Edit Competency' : 'Add Competency'} maxWidth="480px"
        footer={<><button onClick={() => setCompModal(null)} style={btnStyle('#6B7280')}>Cancel</button><button onClick={handleSaveComp} disabled={saving} style={{ ...btnStyle(theme.primary), color: '#fff', background: theme.primary }}>{saving ? 'Saving…' : 'Save'}</button></>}>
        <div style={FG}><label style={LBL}>Name *</label><input style={INP} value={compForm.name} onChange={e => setCompForm(f => ({...f, name: e.target.value}))} /></div>
        <div style={FG}><label style={LBL}>Description</label><textarea style={{ ...INP, minHeight: '60px' }} value={compForm.description} onChange={e => setCompForm(f => ({...f, description: e.target.value}))} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={FG}><label style={LBL}>Category</label><input style={INP} value={compForm.category} onChange={e => setCompForm(f => ({...f, category: e.target.value}))} placeholder="e.g. Technical" /></div>
          <div style={FG}><label style={LBL}>Department (optional)</label><input style={INP} value={compForm.department} onChange={e => setCompForm(f => ({...f, department: e.target.value}))} placeholder="e.g. Engineering" /></div>
          <div style={FG}><label style={LBL}>Expected Level</label>
            <select style={INP} value={compForm.expected_level} onChange={e => setCompForm(f => ({...f, expected_level: e.target.value}))}>
              {LEVELS.map(l => <option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Main Training Page ───────────────────────────────────────────────────────

function Training() {
  const [tab, setTab] = useState('programs');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    Promise.allSettled([
      hrms.getEmployees({ limit: 500 }),
      hrms.getDepartments(),
    ]).then(([empRes, deptRes]) => {
      if (empRes.status === 'fulfilled') {
        const d = empRes.value.data;
        setEmployees(Array.isArray(d) ? d : d.items || []);
      }
      if (deptRes.status === 'fulfilled') setDepartments(deptRes.value.data || []);
    });
  }, []);

  const TABS = [
    { key: 'programs', label: '📋 Training Programs' },
    { key: 'calendar', label: '📅 Training Calendar' },
    { key: 'skillmatrix', label: '🎯 Skill Matrix' },
  ];

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: '0 0 0.25rem' }}>Training & Development</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: 0 }}>Manage training plans, schedules, and employee competencies</p>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', background: '#F3F4F6', borderRadius: '0.625rem', padding: '0.25rem', width: 'fit-content' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: '0.5rem 1.1rem', border: 'none', borderRadius: '0.5rem', fontWeight: tab === t.key ? 700 : 500, fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.15s', background: tab === t.key ? '#fff' : 'transparent', color: tab === t.key ? theme.primary : '#6B7280', boxShadow: tab === t.key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'programs' && <TrainingPrograms employees={employees} departments={departments} />}
      {tab === 'calendar' && <TrainingCalendar />}
      {tab === 'skillmatrix' && <SkillMatrix departments={departments} />}
    </div>
  );
}

export default Training;

// ─── Shared Styles ────────────────────────────────────────────────────────────
const TH = { padding: '0.65rem 0.75rem', color: '#D1D5DB', fontSize: '0.78rem', fontWeight: 600, textAlign: 'center', border: 'none', whiteSpace: 'nowrap' };
const navBtn = { background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '0.375rem', width: '32px', height: '32px', cursor: 'pointer', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' };
function btnStyle(color) { return { padding: '0.4rem 0.875rem', border: `1px solid ${color}20`, borderRadius: '0.375rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', color, background: color + '10' }; }
